import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Loader2, Image } from 'lucide-react';
import { fileAnalyzer } from '@/utils/fileAnalyzer';
import SmartUploadHandler from '@/services/smartUploadHandler';

interface UploadedFile {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  uploadDate: string;
  clientCode: string;
  category: string;
  context: string;
  analysis?: any;
  isVector?: boolean;
}

interface UploadContext {
  page: 'arch-analysis' | 'static-code-analysis' | 'wsr' | 'vm-profile' | 'general' | 'database-analysis';
  title: string;
  description: string;
  expectedFileTypes: string[];
  analysisType: string;
  handler: string;
  instructions: string[];
}

interface FileUploadProps {
  clientCode: string;
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess?: (files: UploadedFile[]) => void;
  context: UploadContext;
}

export default function ContextAwareFileUpload({ 
  clientCode, 
  isOpen, 
  onClose, 
  onUploadSuccess, 
  context 
}: FileUploadProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    setError(null);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
    setError(null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const removeFile = (index: number) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  const getFileIcon = (file: File) => {
    const extension = file.name.toLowerCase();
    if (extension.endsWith('.svg') || file.type === 'image/svg+xml') {
      return <Image className="w-5 h-5 text-purple-600 dark:text-purple-400" />;
    }
    return <FileText className="w-5 h-5 text-shiftleft-600 dark:text-shiftleft-400" />;
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const uploadedFiles: UploadedFile[] = [];
      const totalFiles = selectedFiles.length;

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        // Update progress for file processing
        setUploadProgress(Math.floor((i / totalFiles) * 80));
        
        try {
          // Use Smart Upload Handler for context-aware processing
          console.log(`🚀 Smart Upload: Processing ${file.name} for ${context.handler} (${context.page})`);
          
          const smartHandler = SmartUploadHandler.getInstance();
          const result = await smartHandler.handleUpload(file, context, clientCode);
          
          console.log(`✅ Smart Upload Result:`, result);
          
          // Create uploaded file record with smart analysis
          const uploadedFile: UploadedFile = {
            id: `${context.page}-${Date.now()}-${i}`,
            name: file.name,
            type: file.type,
            size: file.size,
            url: URL.createObjectURL(file),
            uploadDate: new Date().toISOString(),
            clientCode,
            category: context.page,
            context: context.page,
            analysis: {
              context: context.page,
              fileType: file.type || 'Unknown',
              summary: result.message,
              keyFindings: result.success ? ['File processed successfully'] : ['Processing failed'],
              recommendations: result.success ? ['Analysis completed'] : ['Check file format and try again'],
              technicalDetails: result.data || {},
              smartAnalysis: result,
              analysisType: result.analysisType
            },
            isVector: file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')
          };

          uploadedFiles.push(uploadedFile);
          
          // For WSR uploads, trigger the WSR upload service callback
          if (context.page === 'wsr' && result.success && result.data) {
            console.log('🔄 WSR Upload: Triggering analysis callback for WSR pre-population');
            // The WSRUploadService will handle the callback automatically
          }
          
        } catch (fileError) {
          console.error(`Error analyzing file ${file.name}:`, fileError);
          // Continue with other files, but log the error
          const uploadedFile: UploadedFile = {
            id: `${context.page}-${Date.now()}-${i}`,
            name: file.name,
            type: file.type,
            size: file.size,
            url: URL.createObjectURL(file),
            uploadDate: new Date().toISOString(),
            clientCode,
            category: context.page,
            context: context.page,
            analysis: {
              context: context.page,
              fileType: file.type || 'Unknown',
              summary: `Error analyzing ${file.name}: ${fileError.message || 'Unknown error'}`,
              keyFindings: ['File upload successful but analysis failed'],
              recommendations: ['Try re-uploading the file', 'Check file format and content'],
              technicalDetails: { error: fileError.message }
            },
            isVector: file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')
          };
          uploadedFiles.push(uploadedFile);
        }
      }

      setUploadProgress(90);

      // Store with context-specific key
      const storageKey = `${context.page}_files_${clientCode}`;
      const existingFiles = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const updatedFiles = [...existingFiles, ...uploadedFiles];
      localStorage.setItem(storageKey, JSON.stringify(updatedFiles));

      setUploadProgress(100);

      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess(uploadedFiles);
      }

      // Close dialog after successful upload
      setTimeout(() => {
        onClose();
        setSelectedFiles([]);
        setUploadProgress(0);
      }, 500);

    } catch (error) {
      console.error('Upload error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred during upload');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isValidFileType = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    return extension ? context.expectedFileTypes.includes(extension) : false;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {context.title}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {context.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
          {/* Context Information */}
          <div className="mb-4 p-3 bg-shiftleft-50 dark:bg-shiftleft-900/20 rounded-lg border border-shiftleft-200 dark:border-shiftleft-800">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-shiftleft-600 dark:text-shiftleft-400" />
              <span className="text-sm font-medium text-shiftleft-800 dark:text-shiftleft-200">
                Analysis Type: {context.analysisType}
              </span>
            </div>
            <p className="text-xs text-shiftleft-700 dark:text-shiftleft-300">
              Expected file types: {context.expectedFileTypes.join(', ')}
            </p>
          </div>

          {/* Smart Upload Instructions */}
          {context.instructions && context.instructions.length > 0 && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-800 dark:text-green-200">
                  Smart Analysis Features
                </span>
              </div>
              <ul className="text-xs text-green-700 dark:text-green-300 space-y-1">
                {context.instructions.map((instruction, index) => (
                  <li key={index} className="flex items-start gap-1">
                    <span className="text-green-500 mt-0.5">•</span>
                    <span>{instruction}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                <span className="text-sm font-medium text-red-800 dark:text-red-200">
                  Upload Error
                </span>
              </div>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                {error}
              </p>
            </div>
          )}

          {/* Upload Area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-shiftleft-500 dark:hover:border-shiftleft-400 transition-colors"
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Choose files or drag them here
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Upload {context.expectedFileTypes.join(', ')} files for {context.analysisType.toLowerCase()}
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept={context.expectedFileTypes.map(type => `.${type}`).join(',')}
          />

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="space-y-2">
                {selectedFiles.map((file, index) => {
                  const isValid = isValidFileType(file);
                  const isSVG = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
                  
                  return (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        isValid 
                          ? 'bg-gray-50 dark:bg-gray-700' 
                          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {getFileIcon(file)}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {file.name}
                            </p>
                            {isSVG && (
                              <span className="text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-600 px-2 py-1 rounded">
                                SVG
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(file.size)}
                            {!isValid && (
                              <span className="text-red-600 dark:text-red-400 ml-2">
                                • Unsupported file type
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Analyzing files...
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {uploadProgress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-shiftleft-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Processing files and performing {context.analysisType.toLowerCase()}...
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Client: <span className="font-medium text-gray-900 dark:text-white">{clientCode}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={uploadFiles}
              disabled={selectedFiles.length === 0 || uploading}
              className="px-4 py-2 text-sm font-medium text-white bg-shiftleft-600 border border-transparent rounded-md hover:bg-shiftleft-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload & Analyze ({selectedFiles.length})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}